export interface ScreenCaptureSnapshot {
  blob: Blob;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
}

export interface ScreenCaptureTarget {
  snapshot: (signal: AbortSignal) => Promise<ScreenCaptureSnapshot>;
  onStop: (callback: () => void) => () => void;
  setRegionOverride: (element: HTMLElement | null) => void;
  dispose: () => void;
}

interface TargetEntry {
  element: HTMLElement;
  regionOverride: HTMLElement | null;
  stopListeners: Set<() => void>;
  disposed: boolean;
}

interface SnapshotRequest {
  target: TargetEntry;
  signal: AbortSignal;
  resolve: (snapshot: ScreenCaptureSnapshot) => void;
  reject: (error: unknown) => void;
}

interface CaptureSession {
  targets: Set<TargetEntry>;
  abortController: AbortController;
  stream: MediaStream | null;
  track: MediaStreamTrack | null;
  video: HTMLVideoElement | null;
  ready: Promise<void>;
  initialDelay: Promise<void>;
  operationQueue: Promise<void>;
  croppedRoot: HTMLElement | null;
  cropSupported: boolean;
  pendingBatch: SnapshotRequest[] | null;
  stopped: boolean;
}

interface CropTargetConstructor {
  fromElement: (element: Element) => Promise<unknown>;
}

const SNAPSHOT_CLASS = 'media_viewer_website_snapshot--in-progress';
const FRAME_FLUSH_COUNT = 3;
const BLACK_PIXEL_THRESHOLD = 8;
const BLACK_FRAME_MAX_WAIT_MS = 3000;

let activeSession: CaptureSession | null = null;
const registeredTargets = new Set<TargetEntry>();

function createAbortError() {
  return new DOMException('The operation was aborted.', 'AbortError');
}

function getCropTargetConstructor(): CropTargetConstructor | null {
  return (
    (window as typeof window & { CropTarget?: CropTargetConstructor })
      .CropTarget ?? null
  );
}

export function isCurrentTabScreenCaptureSupported() {
  return (
    typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
    typeof getCropTargetConstructor()?.fromElement === 'function'
  );
}

function containsAll(ancestor: HTMLElement, elements: HTMLElement[]) {
  return elements.every((element) => ancestor.contains(element));
}

function getLowestCommonAncestor(elements: HTMLElement[]) {
  if (!elements.length) return null;
  let ancestor: HTMLElement | null = elements[0];
  while (ancestor && !containsAll(ancestor, elements)) {
    ancestor = ancestor.parentElement;
  }
  return ancestor;
}

function getConnectedTargets(session: CaptureSession) {
  return Array.from(session.targets).filter(
    (target) => !target.disposed && target.element.isConnected
  );
}

function getEffectiveRegionElement(target: TargetEntry) {
  return target.regionOverride?.isConnected
    ? target.regionOverride
    : target.element;
}

function enqueueOperation<T>(
  session: CaptureSession,
  operation: () => Promise<T>
): Promise<T> {
  const result = session.operationQueue.then(() => {
    if (session.stopped) throw createAbortError();
    return operation();
  });
  session.operationQueue = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

function notifyStopped(session: CaptureSession) {
  session.targets.forEach((target) => {
    target.stopListeners.forEach((callback) => callback());
  });
}

function stopSession(session: CaptureSession, notify: boolean) {
  if (session.stopped) return;
  session.stopped = true;
  session.abortController.abort();
  if (activeSession === session) activeSession = null;
  document.body.classList.remove(SNAPSHOT_CLASS);
  session.pendingBatch?.forEach((request) => rejectIfPending(request));
  session.pendingBatch = null;
  if (notify) notifyStopped(session);
  session.stream?.getTracks().forEach((track) => track.stop());
  session.stream = null;
  if (session.video) session.video.srcObject = null;
  session.video = null;
  session.track = null;
}

function rejectIfPending(request: SnapshotRequest) {
  request.reject(createAbortError());
}

async function initializeSession(session: CaptureSession) {
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      audio: false,
      video: {
        displaySurface: 'browser',
        cursor: 'never',
        frameRate: { ideal: 30 },
        contentHint: 'text',
        resizeMode: 'none'
      },
      selfBrowserSurface: 'include',
      preferCurrentTab: true,
      monitorTypeSurfaces: 'exclude',
      surfaceSwitching: 'exclude',
      systemAudio: 'exclude',
      windowAudio: 'exclude'
    } as DisplayMediaStreamOptions);
    if (session.stopped) {
      stream.getTracks().forEach((track) => track.stop());
      throw createAbortError();
    }

    const [track] = stream.getVideoTracks();
    if (!track) {
      throw new Error('Display capture did not provide a video track');
    }
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    video.addEventListener('loadedmetadata', () => {
      video.play().catch(() => undefined);
    });
    track.addEventListener('ended', () => stopSession(session, true), {
      once: true
    });

    session.stream = stream;
    session.track = track;
    session.video = video;
    session.cropSupported =
      !!getCropTargetConstructor() &&
      typeof (track as MediaStreamTrack & { cropTo?: unknown }).cropTo ===
        'function';
    await syncCaptureRegion(session);
  } catch (error) {
    stopSession(session, false);
    throw error;
  }
}

function createSession(): CaptureSession {
  let resolveInitialDelay: () => void;
  const initialDelay = new Promise<void>((resolve) => {
    resolveInitialDelay = resolve;
  });
  window.setTimeout(() => resolveInitialDelay(), 2500);

  const session = {
    targets: new Set(registeredTargets),
    abortController: new AbortController(),
    stream: null,
    track: null,
    video: null,
    ready: Promise.resolve(),
    initialDelay,
    operationQueue: Promise.resolve(),
    croppedRoot: null,
    cropSupported: false,
    pendingBatch: null,
    stopped: false
  } satisfies CaptureSession;
  session.ready = initializeSession(session);
  session.ready.catch(() => undefined);
  return session;
}

function getOrCreateSession() {
  if (activeSession) return activeSession;
  const session = createSession();
  activeSession = session;
  return session;
}

async function syncCaptureRegion(session: CaptureSession) {
  if (!session.cropSupported) return;
  const track = session.track as MediaStreamTrack & {
    cropTo: (target: unknown) => Promise<void>;
  };
  const cropTargetConstructor = getCropTargetConstructor();
  const root = getLowestCommonAncestor(
    getConnectedTargets(session).map(getEffectiveRegionElement)
  );
  if (!root || root === session.croppedRoot || !cropTargetConstructor) return;
  const cropTarget = await cropTargetConstructor.fromElement(root);
  await track.cropTo(cropTarget);
  session.croppedRoot = root;
}

function scheduleRegionSync(session: CaptureSession) {
  if (!session.track || !session.cropSupported || session.stopped) return;
  enqueueOperation(session, () => syncCaptureRegion(session)).catch((error) => {
    console.error('Failed to update display capture region', error);
    stopSession(session, true);
  });
}

function scheduleRegionOverrideSync(
  session: CaptureSession,
  target: TargetEntry,
  regionOverride: HTMLElement | null
) {
  if (!session.track || !session.cropSupported || session.stopped) return;
  enqueueOperation(session, () => syncCaptureRegion(session)).catch((error) => {
    if (target.regionOverride !== regionOverride) return;
    target.regionOverride = null;
    console.error('Failed to update display capture region override', error);
    scheduleRegionSync(session);
  });
}

function waitForAnimationFrame() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

function drawVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  context: CanvasRenderingContext2D
) {
  const { videoWidth, videoHeight } = video;
  if (!videoWidth || !videoHeight) return false;
  if (canvas.width !== videoWidth) canvas.width = videoWidth;
  if (canvas.height !== videoHeight) canvas.height = videoHeight;
  // Resizing the canvas resets context state.
  context.imageSmoothingEnabled = false;
  context.drawImage(video, 0, 0, videoWidth, videoHeight);
  return true;
}

function isCanvasBlack(
  context: CanvasRenderingContext2D,
  width: number,
  height: number
) {
  const { data } = context.getImageData(0, 0, width, height);
  for (let i = 0; i < data.length; i += 4) {
    if (
      data[i + 3] &&
      (data[i] > BLACK_PIXEL_THRESHOLD ||
        data[i + 1] > BLACK_PIXEL_THRESHOLD ||
        data[i + 2] > BLACK_PIXEL_THRESHOLD)
    ) {
      return false;
    }
  }
  return true;
}

function waitForFreshFrame(
  video: HTMLVideoElement,
  signal: AbortSignal,
  repaintTime: number
) {
  signal.throwIfAborted();
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('Failed to create snapshot canvas context');

  return new Promise<HTMLCanvasElement>((resolve, reject) => {
    let settled = false;
    const settle = (error?: unknown) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(deadlineTimeout);
      signal.removeEventListener('abort', onAbort);
      if (error === undefined) resolve(canvas);
      else reject(error);
    };
    const onAbort = () => settle(createAbortError());
    signal.addEventListener('abort', onAbort, { once: true });

    // Accept whatever is on screen at the deadline: the page may be genuinely dark or emit no new frames.
    const deadlineTimeout = window.setTimeout(() => {
      try {
        if (drawVideoFrame(video, canvas, context)) settle();
        else settle(new Error('Captured video frame has invalid dimensions'));
      } catch (error) {
        settle(error);
      }
    }, Math.max(0, repaintTime + BLACK_FRAME_MAX_WAIT_MS - performance.now()));

    // Chrome sometimes delivers pitch-black frames right after capture starts.
    const tryAcceptFrame = () => {
      try {
        if (
          !drawVideoFrame(video, canvas, context) ||
          isCanvasBlack(context, canvas.width, canvas.height)
        ) {
          return false;
        }
        settle();
      } catch (error) {
        settle(error);
      }
      return true;
    };

    if (typeof video.requestVideoFrameCallback === 'function') {
      let framesChecked = 0;
      const checkFrame: VideoFrameRequestCallback = (_, metadata) => {
        if (settled) return;
        framesChecked += 1;
        const isFresh =
          (metadata.captureTime && metadata.captureTime > repaintTime) ||
          framesChecked >= FRAME_FLUSH_COUNT;
        if (isFresh && tryAcceptFrame()) return;
        video.requestVideoFrameCallback(checkFrame);
      };
      video.requestVideoFrameCallback(checkFrame);
      return;
    }

    const initialFrames = video.getVideoPlaybackQuality().totalVideoFrames;
    const targetFrames = initialFrames + FRAME_FLUSH_COUNT;
    let lastCheckedFrames = -1;
    const checkFrame = () => {
      if (settled) return;
      const totalFrames = video.getVideoPlaybackQuality().totalVideoFrames;
      if (totalFrames >= targetFrames && totalFrames !== lastCheckedFrames) {
        lastCheckedFrames = totalFrames;
        if (tryAcceptFrame()) return;
      }
      requestAnimationFrame(checkFrame);
    };
    requestAnimationFrame(checkFrame);
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, signal: AbortSignal) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (signal.aborted) {
        reject(createAbortError());
      } else if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to create blob'));
      }
    }, 'image/png');
  });
}

async function cropSnapshot(
  source: HTMLCanvasElement,
  referenceRect: DOMRect,
  targetRect: DOMRect,
  signal: AbortSignal
): Promise<ScreenCaptureSnapshot> {
  signal.throwIfAborted();
  if (!referenceRect.width || !referenceRect.height) {
    throw new Error('Display capture region has invalid dimensions');
  }

  const scaleX = source.width / referenceRect.width;
  const scaleY = source.height / referenceRect.height;
  const rawX = (targetRect.left - referenceRect.left) * scaleX;
  const rawY = (targetRect.top - referenceRect.top) * scaleY;
  const sourceX = Math.max(0, Math.min(source.width, rawX));
  const sourceY = Math.max(0, Math.min(source.height, rawY));
  const sourceWidth = Math.max(
    1,
    Math.min(source.width - sourceX, targetRect.width * scaleX)
  );
  const sourceHeight = Math.max(
    1,
    Math.min(source.height - sourceY, targetRect.height * scaleY)
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(sourceWidth));
  canvas.height = Math.max(1, Math.round(sourceHeight));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Failed to create snapshot canvas context');
  context.imageSmoothingEnabled = false;
  context.drawImage(
    source,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    canvas.width,
    canvas.height
  );
  const blob = await canvasToBlob(canvas, signal);
  return {
    blob,
    width: canvas.width,
    height: canvas.height,
    sourceWidth: targetRect.width,
    sourceHeight: targetRect.height
  };
}

async function processBatch(
  session: CaptureSession,
  requests: SnapshotRequest[]
) {
  await session.ready;
  await session.initialDelay;
  const activeRequests = requests.filter(
    ({ signal, target }) =>
      !signal.aborted && !target.disposed && target.element.isConnected
  );
  requests
    .filter((request) => !activeRequests.includes(request))
    .forEach(rejectIfPending);
  if (!activeRequests.length) return;

  try {
    await syncCaptureRegion(session);
  } catch (error) {
    stopSession(session, true);
    throw error;
  }
  const { video } = session;
  if (!video) throw createAbortError();
  document.body.classList.add(SNAPSHOT_CLASS);
  try {
    const _ = document.body.offsetHeight;
    await waitForAnimationFrame();
    await waitForAnimationFrame();
    session.abortController.signal.throwIfAborted();
    const repaintTime = performance.now();
    const source = await waitForFreshFrame(
      video,
      session.abortController.signal,
      repaintTime
    );

    const referenceRect = session.cropSupported
      ? session.croppedRoot?.getBoundingClientRect()
      : new DOMRect(0, 0, window.innerWidth, window.innerHeight);
    if (!referenceRect) {
      throw new Error('Display capture region is unavailable');
    }
    await Promise.all(
      activeRequests.map(async (request) => {
        try {
          const targetRect = request.target.element.getBoundingClientRect();
          request.resolve(
            await cropSnapshot(
              source,
              referenceRect,
              targetRect,
              request.signal
            )
          );
        } catch (error) {
          request.reject(error);
        }
      })
    );
  } finally {
    document.body.classList.remove(SNAPSHOT_CLASS);
  }
}

function flushBatch(session: CaptureSession) {
  const requests = session.pendingBatch ?? [];
  session.pendingBatch = null;
  enqueueOperation(session, () => processBatch(session, requests)).catch(
    (error) => {
      requests.forEach((request) => request.reject(error));
    }
  );
}

function requestSnapshot(
  session: CaptureSession,
  target: TargetEntry,
  signal: AbortSignal
) {
  return new Promise<ScreenCaptureSnapshot>((resolve, reject) => {
    if (signal.aborted || target.disposed || session.stopped) {
      reject(createAbortError());
      return;
    }
    const request = { target, signal, resolve, reject };
    if (session.pendingBatch) {
      session.pendingBatch.push(request);
      return;
    }
    session.pendingBatch = [request];
    queueMicrotask(() => flushBatch(session));
  });
}

export function registerScreenCaptureTarget(
  element: HTMLElement,
  signal: AbortSignal
): ScreenCaptureTarget {
  signal.throwIfAborted();
  const target: TargetEntry = {
    element,
    regionOverride: null,
    stopListeners: new Set(),
    disposed: false
  };
  registeredTargets.add(target);
  if (activeSession) {
    activeSession.targets.add(target);
    scheduleRegionSync(activeSession);
  }

  const dispose = () => {
    if (target.disposed) return;
    target.disposed = true;
    target.stopListeners.clear();
    registeredTargets.delete(target);
    if (activeSession) {
      activeSession.targets.delete(target);
      if (!activeSession.targets.size) {
        stopSession(activeSession, false);
      } else {
        scheduleRegionSync(activeSession);
      }
    }
  };
  signal.addEventListener('abort', dispose, { once: true });

  return {
    dispose,
    setRegionOverride: (regionOverride) => {
      if (target.disposed || target.regionOverride === regionOverride) return;
      target.regionOverride = regionOverride;
      const session = activeSession;
      if (!session || !session.targets.has(target)) return;
      scheduleRegionOverrideSync(session, target, regionOverride);
    },
    snapshot: (snapshotSignal) => {
      snapshotSignal.throwIfAborted();
      if (target.disposed) return Promise.reject(createAbortError());
      return requestSnapshot(getOrCreateSession(), target, snapshotSignal);
    },
    onStop: (callback) => {
      target.stopListeners.add(callback);
      return () => target.stopListeners.delete(callback);
    }
  };
}
